import { Card } from '@/components/ui/Card'
import { History, TrendingUp, TrendingDown } from 'lucide-react'
import type { CreditHistoryItem } from '@/types'

interface Props {
  history: CreditHistoryItem[]
}

export function CreditHistory({ history }: Props) {
  // Trier par date décroissante (plus récent en premier)
  const sortedHistory = [...history].sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const getOperationLabel = (item: CreditHistoryItem) => {
    // Traiter les raisons "Sync from Tessan (X points)"
    if (item.reason) {
      const syncMatch = item.reason.match(/Sync from Tessan \((\d+) points\)/)
      if (syncMatch) {
        return `Points convertis (${syncMatch[1]} pts)`
      }
      return item.reason
    }

    if (item.origin === 'api') {
      return item.type === 'credit' ? 'Points convertis' : 'Achat boutique'
    }

    return item.type === 'credit' ? 'Crédits ajoutés' : 'Crédits dépensés'
  }

  if (history.length === 0) {
    return (
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <History size={20} className="text-gray-400" />
          Historique de mes crédits
        </h2>
        <p className="text-gray-500 text-center py-8">
          Aucun historique de crédits pour le moment.
        </p>
      </Card>
    )
  }

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <History size={20} className="text-gray-400" />
        Historique de mes crédits
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Date</th>
              <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Opération</th>
              <th className="text-right py-3 px-2 text-sm font-medium text-gray-500">Montant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedHistory.slice(0, 10).map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="py-3 px-2 text-sm text-gray-600">
                  {formatDate(item.created_at)}
                </td>
                <td className="py-3 px-2 text-sm text-gray-900">
                  {getOperationLabel(item)}
                </td>
                <td className="py-3 px-2 text-right">
                  <span
                    className={`inline-flex items-center gap-1 text-sm font-medium ${
                      item.type === 'credit'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {item.type === 'credit' ? (
                      <TrendingUp size={14} />
                    ) : (
                      <TrendingDown size={14} />
                    )}
                    {item.type === 'credit' ? '+' : '-'}{Math.abs(item.value)} créd.
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {history.length > 10 && (
        <p className="text-center text-sm text-gray-400 mt-4">
          Affichage des 10 dernières opérations
        </p>
      )}
    </Card>
  )
}
